import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventFormSubmissionApi } from '../api/eventFormSubmissions';
import type { FormResponses, FormSubmissionResult } from '../types/publicForm.types';

// Query keys
export const eventFormKeys = {
  all: ['portal', 'eventForms'] as const,
  page: (eventSlug: string, formSlug: string, pageIndex: number) => 
    [...eventFormKeys.all, 'page', eventSlug, formSlug, pageIndex] as const,
  draft: (eventSlug: string, formSlug: string) => 
    [...eventFormKeys.all, 'draft', eventSlug, formSlug] as const,
};

// Get form page in event context
export const useEventFormPage = (eventSlug: string, formSlug: string, pageIndex: number) => {
  return useQuery({
    queryKey: eventFormKeys.page(eventSlug, formSlug, pageIndex),
    queryFn: () => eventFormSubmissionApi.getFormPage(eventSlug, formSlug, pageIndex),
    enabled: !!eventSlug && !!formSlug && pageIndex >= 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Submit form in event context
export const useSubmitEventForm = () => {
  const queryClient = useQueryClient();

  return useMutation<FormSubmissionResult, Error, { 
    eventSlug: string; 
    formSlug: string; 
    responses: FormResponses 
  }>({
    mutationFn: ({ eventSlug, formSlug, responses }) => 
      eventFormSubmissionApi.submitForm(eventSlug, formSlug, responses),
    onSuccess: (data, variables) => {
      // Invalidate related queries after successful submission
      queryClient.invalidateQueries({ 
        queryKey: eventFormKeys.page(variables.eventSlug, variables.formSlug, 0) 
      });
    },
  });
};

// Upload file in event context
export const useUploadEventFormFile = () => {
  return useMutation<any, Error, { 
    eventSlug: string; 
    formSlug: string; 
    file: File 
  }>({
    mutationFn: async ({ eventSlug, formSlug, file }) => {
      // Get presigned URL
      const presignedData = await eventFormSubmissionApi.getUploadUrl(
        eventSlug, 
        formSlug, 
        file.name, 
        file.type
      );
      
      // Upload to S3
      const formData = new FormData();
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);
      
      const response = await fetch(presignedData.url, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      return { key: presignedData.key };
    },
  });
};

// Validate field in event context
export const useValidateEventFormField = () => {
  return useMutation<
    { valid: boolean; error?: string },
    Error,
    { eventSlug: string; formSlug: string; questionId: string; value: any }
  >({
    mutationFn: ({ eventSlug, formSlug, questionId, value }) => 
      eventFormSubmissionApi.validateField(eventSlug, formSlug, questionId, value),
  });
};